import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import { Separator } from "../components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Sun, Moon, Loader2, Building, Mail, Phone, MapPin, FileText, Key, Eye, EyeOff, TrendingUp, Lock, ChevronRight, Globe, Users, Copy, Gift, CheckCircle, CalendarDays, Upload, Image, Trash2, Store, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import ModuleSettings from "../components/ModuleSettings";

// Retailer Settings Card Component
function RetailerSettingsCard() {
  const { token } = useAuth();
  const API = process.env.REACT_APP_BACKEND_URL;
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [settings, setSettings] = React.useState({
    accept_retailers: false,
    invite_code: null,
    auto_approve: false,
    default_markup_percent: 0
  });
  const [codeCopied, setCodeCopied] = React.useState(false);

  React.useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/api/tenants/retailer-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(res.data);
    } catch (err) {
      console.error("Failed to fetch retailer settings:", err);
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = async (updates) => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/api/tenants/retailer-settings`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(res.data);
      toast.success("Settings updated");
    } catch (err) {
      toast.error("Failed to update settings");
    } finally {
      setSaving(false);
    }
  };

  const generateInviteCode = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/api/tenants/retailer-settings`, 
        { generate_invite_code: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSettings(res.data);
      toast.success("New invite code generated");
    } catch (err) {
      toast.error("Failed to generate code");
    } finally {
      setSaving(false);
    }
  };

  const copyInviteCode = () => {
    if (settings.invite_code) {
      navigator.clipboard.writeText(settings.invite_code);
      setCodeCopied(true);
      toast.success("Invite code copied!");
      setTimeout(() => setCodeCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <Card className="card-shadow">
        <CardContent className="flex justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="card-shadow border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Store className="w-5 h-5 text-blue-600" />
          Retailer Partnerships (B2B)
        </CardTitle>
        <CardDescription>
          Allow retailers to forward repair jobs to your shop
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Enable/Disable Toggle */}
        <div className="flex items-center justify-between bg-white rounded-lg p-4 shadow-sm">
          <div>
            <Label className="text-gray-900 font-medium">Accept Retailer Partners</Label>
            <p className="text-sm text-gray-500">Allow retailers to request partnership</p>
          </div>
          <Switch
            checked={settings.accept_retailers}
            onCheckedChange={(checked) => updateSettings({ accept_retailers: checked })}
            disabled={saving}
          />
        </div>

        {settings.accept_retailers && (
          <>
            {/* Auto-Approve Toggle */}
            <div className="flex items-center justify-between bg-white rounded-lg p-4 shadow-sm">
              <div>
                <Label className="text-gray-900 font-medium">Auto-Approve Requests</Label>
                <p className="text-sm text-gray-500">Automatically approve partnership requests</p>
              </div>
              <Switch
                checked={settings.auto_approve}
                onCheckedChange={(checked) => updateSettings({ auto_approve: checked })}
                disabled={saving}
              />
            </div>

            {/* Invite Code */}
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <Label className="text-gray-500 text-sm">Invite Code</Label>
              <p className="text-xs text-gray-400 mb-2">Share this code with retailers for instant partnership</p>
              {settings.invite_code ? (
                <div className="flex items-center justify-between">
                  <span className="font-mono font-bold text-lg text-gray-900">{settings.invite_code}</span>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={copyInviteCode}
                      className="border-blue-300"
                    >
                      {codeCopied ? <CheckCircle className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={generateInviteCode}
                      disabled={saving}
                      className="border-blue-300"
                    >
                      <RefreshCw className={`w-4 h-4 ${saving ? 'animate-spin' : ''}`} />
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  onClick={generateInviteCode}
                  disabled={saving}
                  className="w-full bg-blue-500 hover:bg-blue-600"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Generate Invite Code
                </Button>
              )}
            </div>

            {/* Default Markup */}
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <Label className="text-gray-500 text-sm">Default Markup %</Label>
              <p className="text-xs text-gray-400 mb-2">Suggested markup retailers can add to job quotes</p>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={settings.default_markup_percent || 0}
                  onChange={(e) => setSettings({ ...settings, default_markup_percent: parseInt(e.target.value) || 0 })}
                  className="w-24"
                />
                <span className="text-gray-500">%</span>
                <Button
                  size="sm"
                  onClick={() => updateSettings({ default_markup_percent: settings.default_markup_percent })}
                  disabled={saving}
                >
                  Save
                </Button>
              </div>
            </div>

            {/* View Retailers Link */}
            <Button
              variant="outline"
              className="w-full border-blue-300 text-blue-700"
              onClick={() => window.location.href = '/retailers'}
            >
              <Users className="w-4 h-4 mr-2" />
              View Partner Retailers
              <ChevronRight className="w-4 h-4 ml-auto" />
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// Popular world currencies
const CURRENCIES = [
  { code: "INR", symbol: "₹", name: "Indian Rupee" },
  { code: "USD", symbol: "$", name: "US Dollar" },
  { code: "EUR", symbol: "€", name: "Euro" },
  { code: "GBP", symbol: "£", name: "British Pound" },
  { code: "AED", symbol: "د.إ", name: "UAE Dirham" },
  { code: "SAR", symbol: "﷼", name: "Saudi Riyal" },
  { code: "AUD", symbol: "A$", name: "Australian Dollar" },
  { code: "CAD", symbol: "C$", name: "Canadian Dollar" },
  { code: "SGD", symbol: "S$", name: "Singapore Dollar" },
  { code: "MYR", symbol: "RM", name: "Malaysian Ringgit" },
  { code: "JPY", symbol: "¥", name: "Japanese Yen" },
  { code: "CNY", symbol: "¥", name: "Chinese Yuan" },
  { code: "KRW", symbol: "₩", name: "South Korean Won" },
  { code: "THB", symbol: "฿", name: "Thai Baht" },
  { code: "IDR", symbol: "Rp", name: "Indonesian Rupiah" },
  { code: "PHP", symbol: "₱", name: "Philippine Peso" },
  { code: "VND", symbol: "₫", name: "Vietnamese Dong" },
  { code: "BDT", symbol: "৳", name: "Bangladeshi Taka" },
  { code: "PKR", symbol: "₨", name: "Pakistani Rupee" },
  { code: "LKR", symbol: "Rs", name: "Sri Lankan Rupee" },
  { code: "NPR", symbol: "₨", name: "Nepalese Rupee" },
  { code: "ZAR", symbol: "R", name: "South African Rand" },
  { code: "NGN", symbol: "₦", name: "Nigerian Naira" },
  { code: "KES", symbol: "KSh", name: "Kenyan Shilling" },
  { code: "EGP", symbol: "E£", name: "Egyptian Pound" },
  { code: "BRL", symbol: "R$", name: "Brazilian Real" },
  { code: "MXN", symbol: "MX$", name: "Mexican Peso" },
  { code: "ARS", symbol: "AR$", name: "Argentine Peso" },
  { code: "CLP", symbol: "CL$", name: "Chilean Peso" },
  { code: "COP", symbol: "CO$", name: "Colombian Peso" },
  { code: "PEN", symbol: "S/", name: "Peruvian Sol" },
  { code: "CHF", symbol: "CHF", name: "Swiss Franc" },
  { code: "SEK", symbol: "kr", name: "Swedish Krona" },
  { code: "NOK", symbol: "kr", name: "Norwegian Krone" },
  { code: "DKK", symbol: "kr", name: "Danish Krone" },
  { code: "PLN", symbol: "zł", name: "Polish Zloty" },
  { code: "CZK", symbol: "Kč", name: "Czech Koruna" },
  { code: "HUF", symbol: "Ft", name: "Hungarian Forint" },
  { code: "RON", symbol: "lei", name: "Romanian Leu" },
  { code: "TRY", symbol: "₺", name: "Turkish Lira" },
  { code: "ILS", symbol: "₪", name: "Israeli Shekel" },
  { code: "QAR", symbol: "﷼", name: "Qatari Riyal" },
  { code: "KWD", symbol: "د.ك", name: "Kuwaiti Dinar" },
  { code: "BHD", symbol: "BD", name: "Bahraini Dinar" },
  { code: "OMR", symbol: "﷼", name: "Omani Rial" },
  { code: "NZD", symbol: "NZ$", name: "New Zealand Dollar" },
  { code: "HKD", symbol: "HK$", name: "Hong Kong Dollar" },
  { code: "TWD", symbol: "NT$", name: "Taiwan Dollar" },
  { code: "RUB", symbol: "₽", name: "Russian Ruble" },
  { code: "UAH", symbol: "₴", name: "Ukrainian Hryvnia" },
];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Settings() {
  const navigate = useNavigate();
  const { tenant, updateTenant, isAdmin } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [formData, setFormData] = useState({
    company_name: tenant?.company_name || "",
    address: tenant?.settings?.address || "",
    phone: tenant?.settings?.phone || "",
    email: tenant?.settings?.email || "",
    footer_text: tenant?.settings?.footer_text || "",
    currency: tenant?.settings?.currency || "INR",
    logo_url: tenant?.settings?.logo_url || "",
    // SEO fields
    area: tenant?.settings?.area || "",
    city: tenant?.settings?.city || "",
    state: tenant?.settings?.state || "",
    pincode: tenant?.settings?.pincode || "",
    services: tenant?.settings?.services || [],
    about_text: tenant?.settings?.about_text || "",
    opening_hours: tenant?.settings?.opening_hours || "",
    business_type: tenant?.settings?.business_type || "MobilePhoneStore",
  });

  const [newService, setNewService] = useState("");
  const [logoUploading, setLogoUploading] = useState(false);

  // Referral state
  const [referralData, setReferralData] = useState(null);
  const [referralLoading, setReferralLoading] = useState(false);
  const [referralCopied, setReferralCopied] = useState(false);

  // Fetch referral data
  React.useEffect(() => {
    fetchReferralData();
  }, []);

  const fetchReferralData = async () => {
    setReferralLoading(true);
    try {
      const response = await axios.get(`${API}/referrals/my-code`);
      setReferralData(response.data);
    } catch (error) {
      console.error("Failed to fetch referral data:", error);
    } finally {
      setReferralLoading(false);
    }
  };

  const copyReferralLink = () => {
    if (referralData?.referral_link) {
      navigator.clipboard.writeText(referralData.referral_link);
      setReferralCopied(true);
      toast.success("Referral link copied!");
      setTimeout(() => setReferralCopied(false), 2000);
    }
  };

  const shareOnWhatsApp = () => {
    if (referralData?.referral_link) {
      const text = `Hey! I'm using AfterSales.pro for my repair shop. Sign up with my referral link and we both get 30 days free when you subscribe! ${referralData.referral_link}`;
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const response = await axios.put(`${API}/tenants/settings`, formData);
      updateTenant(response.data);
      toast.success("Settings saved successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save settings");
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Logo must be less than 2MB");
      return;
    }

    setLogoUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);

      const response = await axios.post(`${API}/upload/logo`, formDataUpload, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setFormData({ ...formData, logo_url: response.data.url });
      toast.success("Logo uploaded! Click 'Save Settings' to apply.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload logo");
    } finally {
      setLogoUploading(false);
    }
  };

  const handleRemoveLogo = () => {
    setFormData({ ...formData, logo_url: "" });
    toast.info("Logo removed. Click 'Save Settings' to apply.");
  };

  const handlePasswordChange = async () => {
    // Validation
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      toast.error("Please fill in all password fields");
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }
    
    setPasswordLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword,
      });
      toast.success("Password changed successfully");
      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto space-y-6 animate-in" data-testid="settings-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold">Settings</h1>
          <p className="text-muted-foreground">Manage your shop settings and preferences</p>
        </div>

        {/* Profit Reports - Admin Only */}
        {isAdmin && (
          <Card 
            className="card-shadow cursor-pointer hover:border-green-500/50 transition-colors"
            onClick={() => navigate("/profit-reports")}
            data-testid="profit-reports-card"
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-green-500/10 flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-green-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold flex items-center gap-2">
                      Profit Reports
                      <Lock className="w-4 h-4 text-muted-foreground" />
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Track expenses and profit for your repair jobs
                    </p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Appointment Settings - Admin Only */}
        {isAdmin && (
          <Card 
            className="card-shadow cursor-pointer hover:border-blue-500/50 transition-colors"
            onClick={() => navigate("/appointment-settings")}
            data-testid="appointment-settings-card"
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center">
                    <CalendarDays className="w-6 h-6 text-blue-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold flex items-center gap-2">
                      Online Booking
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Let customers book appointments online
                    </p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Theme Settings */}
        <Card className="card-shadow">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {theme === "light" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              Appearance
            </CardTitle>
            <CardDescription>Customize how the dashboard looks</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Dark Mode</p>
                <p className="text-sm text-muted-foreground">
                  Switch between light and dark themes
                </p>
              </div>
              <Switch
                checked={theme === "dark"}
                onCheckedChange={toggleTheme}
                data-testid="theme-switch"
              />
            </div>
          </CardContent>
        </Card>

        {/* Shop Information */}
        {isAdmin && (
          <Card className="card-shadow">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building className="w-5 h-5" />
                Shop Information
              </CardTitle>
              <CardDescription>Update your shop details for job sheets and invoices</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Logo Upload */}
              <div className="space-y-2">
                <Label>Shop Logo</Label>
                <p className="text-sm text-muted-foreground">
                  Upload your shop logo to personalize customer booking pages
                </p>
                <div className="flex items-start gap-4">
                  {formData.logo_url ? (
                    <div className="relative group">
                      <img
                        src={formData.logo_url.startsWith("http") ? formData.logo_url : `${process.env.REACT_APP_BACKEND_URL}${formData.logo_url}`}
                        alt="Shop Logo"
                        className="w-24 h-24 object-contain rounded-lg border bg-white"
                      />
                      <button
                        onClick={handleRemoveLogo}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Remove logo"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <div className="w-24 h-24 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center bg-gray-50">
                      <Image className="w-8 h-8 text-gray-400" />
                    </div>
                  )}
                  <div className="flex-1">
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="hidden"
                        disabled={logoUploading}
                      />
                      <Button type="button" variant="outline" disabled={logoUploading} asChild>
                        <span>
                          {logoUploading ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Uploading...
                            </>
                          ) : (
                            <>
                              <Upload className="w-4 h-4 mr-2" />
                              {formData.logo_url ? "Change Logo" : "Upload Logo"}
                            </>
                          )}
                        </span>
                      </Button>
                    </label>
                    <p className="text-xs text-muted-foreground mt-2">
                      PNG, JPG, WEBP or SVG. Max 2MB.
                    </p>
                  </div>
                </div>
              </div>

              <Separator />

              <div className="space-y-2">
                <Label htmlFor="company_name">Shop Name</Label>
                <Input
                  id="company_name"
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  placeholder="Your Shop Name"
                  data-testid="company-name-input"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="shop@example.com"
                    data-testid="email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone" className="flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    Phone
                  </Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+91 98765 43210"
                    data-testid="phone-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="address" className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Address
                </Label>
                <Textarea
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="123 Main Street, City, State"
                  rows={2}
                  data-testid="address-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="currency" className="flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  Currency
                </Label>
                <Select
                  value={formData.currency}
                  onValueChange={(value) => setFormData({ ...formData, currency: value })}
                >
                  <SelectTrigger data-testid="currency-select">
                    <SelectValue placeholder="Select currency" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {CURRENCIES.map((currency) => (
                      <SelectItem key={currency.code} value={currency.code}>
                        <span className="flex items-center gap-2">
                          <span className="w-6 text-center font-mono">{currency.symbol}</span>
                          <span>{currency.code}</span>
                          <span className="text-muted-foreground">- {currency.name}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  This currency will be used for all prices and invoices
                </p>
              </div>

              <Separator />

              <div className="space-y-2">
                <Label htmlFor="footer_text" className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Job Sheet Footer Text
                </Label>
                <Textarea
                  id="footer_text"
                  value={formData.footer_text}
                  onChange={(e) => setFormData({ ...formData, footer_text: e.target.value })}
                  placeholder="Thank you for choosing us!"
                  rows={2}
                  data-testid="footer-text-input"
                />
                <p className="text-xs text-muted-foreground">
                  This text will appear at the bottom of your job sheets
                </p>
              </div>

              <Separator />

              {/* SEO Settings for Google Ranking */}
              <div className="space-y-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h3 className="font-semibold text-blue-900 flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  Google Search Optimization (SEO)
                </h3>
                <p className="text-xs text-blue-700">
                  These fields help your shop page rank higher on Google. Your page: <a href={`https://${tenant?.subdomain}.aftersales.pro`} target="_blank" rel="noopener noreferrer" className="underline">{tenant?.subdomain}.aftersales.pro</a>
                </p>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="area">Area/Locality *</Label>
                    <Input
                      id="area"
                      value={formData.area}
                      onChange={(e) => setFormData({ ...formData, area: e.target.value })}
                      placeholder="e.g., Mulund West"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="city">City *</Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      placeholder="e.g., Mumbai"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="state">State</Label>
                    <Input
                      id="state"
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                      placeholder="e.g., Maharashtra"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pincode">Pincode</Label>
                    <Input
                      id="pincode"
                      value={formData.pincode}
                      onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
                      placeholder="e.g., 400080"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="business_type">Business Type</Label>
                  <Select
                    value={formData.business_type}
                    onValueChange={(value) => setFormData({ ...formData, business_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select business type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="MobilePhoneStore">Mobile Phone Store</SelectItem>
                      <SelectItem value="ElectronicsStore">Electronics Store</SelectItem>
                      <SelectItem value="ComputerStore">Computer Store</SelectItem>
                      <SelectItem value="LocalBusiness">General Repair Shop</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="opening_hours">Opening Hours</Label>
                  <Input
                    id="opening_hours"
                    value={formData.opening_hours}
                    onChange={(e) => setFormData({ ...formData, opening_hours: e.target.value })}
                    placeholder="e.g., Mo-Sa 11:00-20:00"
                  />
                  <p className="text-xs text-muted-foreground">Format: Mo-Sa 11:00-20:00 (shown in Google)</p>
                </div>

                <div className="space-y-2">
                  <Label>Services Offered</Label>
                  <div className="flex gap-2">
                    <Input
                      value={newService}
                      onChange={(e) => setNewService(e.target.value)}
                      placeholder="e.g., iPhone screen replacement"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && newService.trim()) {
                          e.preventDefault();
                          setFormData({ ...formData, services: [...(formData.services || []), newService.trim()] });
                          setNewService("");
                        }
                      }}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        if (newService.trim()) {
                          setFormData({ ...formData, services: [...(formData.services || []), newService.trim()] });
                          setNewService("");
                        }
                      }}
                    >
                      Add
                    </Button>
                  </div>
                  {formData.services && formData.services.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {formData.services.map((service, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-white border rounded-full text-sm"
                        >
                          {service}
                          <button
                            type="button"
                            onClick={() => {
                              const newServices = [...formData.services];
                              newServices.splice(index, 1);
                              setFormData({ ...formData, services: newServices });
                            }}
                            className="text-red-500 hover:text-red-700"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="about_text">About Your Shop</Label>
                  <Textarea
                    id="about_text"
                    value={formData.about_text}
                    onChange={(e) => setFormData({ ...formData, about_text: e.target.value })}
                    placeholder="Tell customers about your shop, expertise, and history..."
                    rows={3}
                  />
                </div>
              </div>

              <Button onClick={handleSave} disabled={loading} data-testid="save-settings-btn">
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save Changes"
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Change Password */}
        <Card className="card-shadow">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="w-5 h-5" />
              Change Password
            </CardTitle>
            <CardDescription>Update your account password</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="currentPassword">Current Password</Label>
              <div className="relative">
                <Input
                  id="currentPassword"
                  type={showCurrentPassword ? "text" : "password"}
                  value={passwordForm.currentPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                  placeholder="Enter current password"
                  data-testid="current-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="newPassword">New Password</Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showNewPassword ? "text" : "password"}
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                  placeholder="Enter new password (min 6 characters)"
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                  placeholder="Confirm new password"
                  data-testid="confirm-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <Button
              onClick={handlePasswordChange}
              disabled={passwordLoading}
              className="w-full sm:w-auto"
              data-testid="change-password-btn"
            >
              {passwordLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Changing...
                </>
              ) : (
                <>
                  <Key className="w-4 h-4 mr-2" />
                  Change Password
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Module Settings - Trading Modules */}
        {isAdmin && <ModuleSettings />}

        {/* Referral Program */}
        <Card className="card-shadow border-green-200 bg-gradient-to-br from-green-50 to-emerald-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Gift className="w-5 h-5 text-green-600" />
              Referral Program
            </CardTitle>
            <CardDescription>
              Refer other shops and both get 30 days free when they subscribe!
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {referralLoading ? (
              <div className="flex justify-center py-4">
                <Loader2 className="w-6 h-6 animate-spin text-green-600" />
              </div>
            ) : referralData ? (
              <>
                {/* Stats */}
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="bg-white rounded-lg p-3 shadow-sm">
                    <p className="text-2xl font-bold text-gray-900">{referralData.total_referrals}</p>
                    <p className="text-xs text-gray-500">Referrals</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 shadow-sm">
                    <p className="text-2xl font-bold text-green-600">{referralData.converted_referrals}</p>
                    <p className="text-xs text-gray-500">Converted</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 shadow-sm">
                    <p className="text-2xl font-bold text-green-600">+{referralData.total_days_earned}</p>
                    <p className="text-xs text-gray-500">Days Earned</p>
                  </div>
                </div>

                {/* Referral Code */}
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <Label className="text-gray-500 text-sm">Your Referral Code</Label>
                  <div className="flex items-center justify-between mt-1">
                    <span className="font-mono font-bold text-lg text-gray-900">{referralData.referral_code}</span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={copyReferralLink}
                      className="border-green-300"
                    >
                      {referralCopied ? <CheckCircle className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>

                {/* Referral Link */}
                <div className="bg-white rounded-lg p-3 shadow-sm">
                  <div className="flex items-center justify-between gap-2">
                    <code className="text-sm text-gray-600 truncate flex-1">
                      {referralData.referral_link}
                    </code>
                  </div>
                </div>

                {/* Share Buttons */}
                <div className="flex gap-2">
                  <Button
                    onClick={copyReferralLink}
                    variant="outline"
                    className="flex-1 border-green-300"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copy Link
                  </Button>
                  <Button
                    onClick={shareOnWhatsApp}
                    className="flex-1 bg-green-500 hover:bg-green-600"
                  >
                    <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    Share on WhatsApp
                  </Button>
                </div>

                <p className="text-xs text-gray-500 text-center">
                  You get +{referralData.reward_per_referral} days when your referral subscribes to a paid plan
                </p>
              </>
            ) : (
              <p className="text-center text-gray-500 py-4">Unable to load referral data</p>
            )}
          </CardContent>
        </Card>

        {/* Retailer Partnership Settings - Admin Only */}
        {isAdmin && <RetailerSettingsCard />}

        {/* Account Info */}
        <Card className="card-shadow">
          <CardHeader>
            <CardTitle>Account Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-muted-foreground">Subdomain</Label>
                <p className="font-mono font-medium">{tenant?.subdomain}.aftersales.pro</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Trial Ends</Label>
                <p className="font-medium">
                  {new Date(tenant?.trial_ends_at).toLocaleDateString("en-IN", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
